import { useEffect } from 'react';
import {
  CONNECT,
  GET_COLLUDER_RESTRICTIONS,
  GOT_COLLUDER_RESTRICTIONS
} from 'constants/eventConstants';
import { useAppDispatch } from 'hooks';
import { buildColluderRestrictionsAction } from 'listeners/colluderListeners';
import { decompressData } from 'utils/functions';

type SocketLike = {
  connected?: boolean;
  emit: (event: string, data?: unknown) => void;
  on: (event: string, handler: (data?: unknown) => void) => void;
  off: (event: string, handler?: (data?: unknown) => void) => void;
};

/**
 * EN-10064 — keep the viewer's colluder lobby markers in sync, entirely over
 * game-ws (no player-api REST dependency).
 *
 *   • Initial state: emit `getColluderRestrictions` on (re)connect; the server
 *     resolves the viewer's full set and replies `gotColluderRestrictions`.
 *   • Live deltas: the same `gotColluderRestrictions` event, pushed by the
 *     colluder-broadcast service on flag create/remove and when a flagged
 *     opponent seats/registers.
 *
 * Both the initial reply and the deltas are full-set replaces, so a single
 * handler covers them. There is intentionally NO lift event — a removed/expired
 * restriction simply arrives as a push with that ID absent (silent lift, D9a).
 */
const useColluderRestrictionsSync = (socket: SocketLike | null | undefined) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!socket) return;

    // Pull the initial set. The emit routes to game-ws (SERVERLESS_EMIT_EVENTS);
    // if game-ws isn't open yet the adapter queues it and flushes on connect.
    const requestInitial = () => socket.emit(GET_COLLUDER_RESTRICTIONS);

    const handlePush = (data?: unknown) => {
      dispatch(buildColluderRestrictionsAction(decompressData(data)));
    };

    socket.on(GOT_COLLUDER_RESTRICTIONS, handlePush);
    socket.on(CONNECT, requestInitial);

    if (socket.connected) {
      requestInitial();
    }

    return () => {
      socket.off(GOT_COLLUDER_RESTRICTIONS, handlePush);
      socket.off(CONNECT, requestInitial);
    };
  }, [socket, dispatch]);
};

export default useColluderRestrictionsSync;
